import React from "react";
import { useTranslations } from "next-intl";
import { TourSummary } from "@/types/dto/pdp/pdp-tour-summary";
import { Box, Stack, Typography } from "@mui/material";
import { OPEN_NIGHT_ACCORDION_EVENT } from "@/components/product-detail-page/itinerary-section/components/pre-post-night-accordion";

interface EnhanceTourCardProps {
  tourSummaryData?: TourSummary;
}

const EnhanceTourCard = ({ tourSummaryData }: EnhanceTourCardProps) => {
  const t = useTranslations("PDP");

  // Common styling for enhance items
  const enhanceItemSx = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    py: "1.6rem"
  };

  const handleScrollToElement = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerHeight = 180; // Account for fixed header + 20px extra offset
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - headerHeight,
        behavior: "smooth"
      });
    }
  };

  const handleScrollAndOpenNightAccordion = (elementId: string, nightType: "pre" | "post") => {
    window.dispatchEvent(
      new CustomEvent(OPEN_NIGHT_ACCORDION_EVENT, { detail: { type: nightType } })
    );
    handleScrollToElement(elementId);
  };

  if (!tourSummaryData) {
    return null;
  }

  return (
    <Box
      data-testid="enhance-tour-container"
      sx={{
        bgcolor: "background.cool_strong",
        borderRadius: "10px",
        p: 2
      }}
    >
      {/* Header */}
      <Typography
        data-testid="enhance-tour-heading"
        sx={{
          fontSize: "1.8rem",
          fontWeight: 600,
          lineHeight: "normal",
          mb: "1.6rem"
        }}
      >
        {t("Enhance Your Tour")}
      </Typography>

      {/* Tour Items Stack */}
      <Stack>
        {/* Pre-Tour Extension */}
        {tourSummaryData?.preExtension?.title && tourSummaryData?.preExtension?.price && (
          <Box data-testid="pre-tour-extension" sx={enhanceItemSx}>
            <Box sx={{ flex: 1 }}>
              <Typography
                data-testid="pre-tour-extension-title"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {t("Pre-Tour Extension")}
              </Typography>
              <Typography
                data-testid="pre-tour-extension-location"
                sx={{ fontSize: "1.6rem", fontWeight: 400 }}
              >
                {tourSummaryData.preExtension.title}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", ml: 2 }}>
              <Typography
                data-testid="pre-tour-extension-price"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {tourSummaryData.preExtension.price} pp*
              </Typography>
              <Typography
                data-testid="pre-tour-extension-see-details"
                component="button"
                onClick={() => handleScrollToElement("enhance-your-tour")}
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  textDecoration: "underline",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "primary.main"
                }}
              >
                {t("See Details")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Pre-Night Stay */}
        {tourSummaryData?.preNightItem && (
          <Box data-testid="pre-night-stay" sx={enhanceItemSx}>
            <Box sx={{ flex: 1 }}>
              <Typography
                data-testid="pre-night-stay-title"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {t("Pre-Night Stay")}
              </Typography>
              <Typography
                data-testid="pre-night-stay-location"
                sx={{ fontSize: "1.6rem", fontWeight: 400 }}
              >
                {tourSummaryData.preNightItem.location}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", ml: 2 }}>
              <Typography
                data-testid="pre-night-stay-price"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {tourSummaryData.preNightItem.price} pp*
              </Typography>
              <Typography
                data-testid="pre-night-stay-see-details"
                component="button"
                onClick={() => handleScrollAndOpenNightAccordion("pre-night-header", "pre")}
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  textDecoration: "underline",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "primary.main"
                }}
              >
                {t("See Details")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Post-Tour Extension */}
        {tourSummaryData?.postExtension && (
          <Box data-testid="post-tour-extension" sx={enhanceItemSx}>
            <Box sx={{ flex: 1 }}>
              <Typography
                data-testid="post-tour-extension-title"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {t("Post-Tour Extension")}
              </Typography>
              <Typography
                data-testid="post-tour-extension-location"
                sx={{ fontSize: "1.6rem", fontWeight: 400 }}
              >
                {tourSummaryData.postExtension.title}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", ml: 2 }}>
              <Typography
                data-testid="post-tour-extension-price"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {tourSummaryData.postExtension.price} pp*
              </Typography>
              <Typography
                data-testid="post-tour-extension-see-details"
                component="button"
                onClick={() => handleScrollToElement("enhance-your-tour")}
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  textDecoration: "underline",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "primary.main"
                }}
              >
                {t("See Details")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Post-Night Stay */}
        {tourSummaryData?.postNightItem && (
          <Box data-testid="post-night-stay" sx={enhanceItemSx}>
            <Box sx={{ flex: 1 }}>
              <Typography
                data-testid="post-night-stay-title"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {t("Post-Night Stay")}
              </Typography>
              <Typography
                data-testid="post-night-stay-location"
                sx={{ fontSize: "1.6rem", fontWeight: 400 }}
              >
                {tourSummaryData.postNightItem.location}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", ml: 2 }}>
              <Typography
                data-testid="post-night-stay-price"
                sx={{ fontSize: "1.6rem", fontWeight: 700, mb: "0.8rem" }}
              >
                {tourSummaryData.postNightItem.price} pp*
              </Typography>
              <Typography
                data-testid="post-night-stay-see-details"
                component="button"
                onClick={() => handleScrollAndOpenNightAccordion("post-night-header", "post")}
                sx={{
                  fontSize: "1.4rem",
                  textDecoration: "underline",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "primary.main"
                }}
              >
                {t("See Details")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Optional Experiences */}
        {tourSummaryData?.excursions?.price && (
          <Box data-testid="optional-excursions" sx={enhanceItemSx}>
            <Box sx={{ flex: 1 }}>
              <Typography
                data-testid="optional-excursions-title"
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  mb: "0.8rem",
                  whiteSpace: "pre-line"
                }}
              >
                {t("Optional Experiences").replace(/\s+/, "\n")}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", ml: 2 }}>
              <Typography data-testid="optional-excursions-price" variant="body1">
                {t("Starting at")}
                <span style={{ fontWeight: 700 }}> {tourSummaryData.excursions.price} pp* </span>
              </Typography>
              <Typography
                data-testid="optional-excursions-see-details"
                component="button"
                onClick={() => handleScrollToElement("optional-experiences")}
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  textDecoration: "underline",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "primary.main"
                }}
              >
                {t("See Details")}
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default EnhanceTourCard;
